import type { RequestFn } from '../http';

export type DuplicateMatchMethod = 'EXACT_SUBJECT' | 'KIAN';

export interface DuplicateGroupTicket {
  id: string;
  ticketNumber: string | null;
  subject: string | null;
  status: string | null;
  partyName: string | null;
  senderEmail: string | null;
  createdDate: string | null;
  responsibleUserId: string | null;
  /** Nur bei KIAN-Gruppen: Confidence 0..1 + Begründung des Vorschlags. */
  confidence?: number | null;
  kianReasoning?: string | null;
}

export interface DuplicateGroup {
  key: string;
  matchMethod: DuplicateMatchMethod;
  subject: string;
  partyName: string | null;
  senderEmail: string | null;
  /** Vorgeschlagenes Master-Ticket (ältestes der Gruppe). */
  masterTicketId: string;
  /** Alle Tickets der Gruppe inkl. Master, aufsteigend nach Erstelldatum. */
  tickets: DuplicateGroupTicket[];
}

export interface DuplicateGroupsResponse {
  groups: DuplicateGroup[];
  windowDays: number;
  /** Tatsächlich verwendetes Fenster [from, to) — to ist null im „letzte N Tage"-Modus. */
  from: string;
  to: string | null;
  duplicateCount: number;
  /** Ab dieser Menge verlangt der Server die Tippbestätigung. */
  confirmThreshold: number;
  bulkLimit: number;
}

/** Muss zu CONFIRM_PHRASE in routes/ticketDuplicates.ts passen. */
export const DUPLICATE_CLOSE_CONFIRM_PHRASE = 'SCHLIESSEN';

/** LINK = schließen & aufs Master verlinken (Default); CLOSE_ONLY = nur schließen, Kommentar Pflicht. */
export type DuplicateCloseMode = 'LINK' | 'CLOSE_ONLY';

/**
 * Phase 3 „Zusammenführen & schließen": was vor dem Schließen ins Master übertragen wird.
 * Nur im LINK-Modus wirksam. Zeitbuchungen bleiben am Duplikat — sie werden auch an
 * geschlossenen Tickets abgerechnet.
 */
export interface DuplicateMergeOptions {
  comments: boolean;
  attachments: boolean;
}

/** Zähler der pro Duplikat übertragenen Objekte. */
export interface DuplicateMergeCounts {
  comments: number;
  attachments: number;
  attachmentsSkipped: number;
}

export interface BulkCloseDuplicatesBody {
  /** Pflicht im LINK-Modus; entfällt bei CLOSE_ONLY. */
  masterTicketId?: string;
  duplicateTicketIds: string[];
  comment?: string;
  /** true → Verweis-/Abschluss-Kommentar kundensichtbar statt intern. */
  commentPublic?: boolean;
  matchMethod?: DuplicateMatchMethod;
  mode?: DuplicateCloseMode;
  confirmPhrase?: string;
  /** Wenn gesetzt: Kommentare/Anhänge vor dem Schließen ins Master übertragen. */
  merge?: DuplicateMergeOptions;
}

export interface BulkCloseDuplicatesResult {
  success: boolean;
  closed: Array<{ ticketId: string; ticketNumber: string | null; merged?: DuplicateMergeCounts }>;
  skipped: Array<{ ticketId: string; ticketNumber: string | null; reason: string }>;
  failed: Array<{ ticketId: string; ticketNumber: string | null; message: string }>;
  masterTicketNumber: string | null;
  message: string;
}

/** KIANs Einschätzung zu einer Dubletten-Gruppe. */
export interface DuplicateAssessment {
  groupKey: string;
  /** MERGE = zusammenlegen, RELATED = verwandt aber eigenständig, DISTINCT = Fehlalarm. */
  verdict: 'MERGE' | 'RELATED' | 'DISTINCT';
  masterTicketId: string | null;
  duplicateTicketIds: string[];
  /** 0..1 */
  confidence: number;
  draftComment: string | null;
  reasoning: string;
}

export function createTicketDuplicatesApi(request: RequestFn) {
  return {
    /**
     * Dubletten-Gruppen (deterministisch + KIAN-Vorschläge) aus dem lokalen Spiegel.
     * Entweder days (letzte N Tage) ODER from/to (ISO) fürs Blättern durch den Bestand.
     */
    async getTicketDuplicateGroups(params: { days?: number; from?: string; to?: string } = {}) {
      const search = new URLSearchParams();
      if (params.from && params.to) {
        search.set('from', params.from);
        search.set('to', params.to);
      } else if (params.days) {
        search.set('days', String(params.days));
      }
      const q = search.size > 0 ? `?${search.toString()}` : '';
      return request<DuplicateGroupsResponse>(`/weclapp/tickets/duplicate-groups${q}`);
    },

    /** Duplikate serverseitig schließen — mit Verweis-Kommentaren in beide Richtungen. */
    /**
     * KIAN prüft eine Gruppe und schlägt vor, was zu tun ist. Führt nichts aus —
     * geschlossen wird erst über bulkCloseDuplicateTickets.
     */
    async assessDuplicateGroup(body: {
      key: string;
      tickets: Array<{
        id: string;
        ticketNumber?: string | null;
        subject?: string | null;
        partyName?: string | null;
        senderEmail?: string | null;
        createdDate?: string | null;
        status?: string | null;
      }>;
    }) {
      return request<{ assessment: DuplicateAssessment }>(
        '/weclapp/tickets/duplicate-groups/assess',
        { method: 'POST', body: JSON.stringify(body) }
      );
    },

    async bulkCloseDuplicateTickets(body: BulkCloseDuplicatesBody) {
      return request<BulkCloseDuplicatesResult>('/weclapp/tickets/duplicates/bulk-close', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    /** Paar als „kein Duplikat" verwerfen — wird nie wieder vorgeschlagen. */
    async dismissTicketDuplicate(body: {
      ticketId: string;
      duplicateOfTicketId: string;
      matchMethod?: DuplicateMatchMethod;
    }) {
      return request<{ success: boolean }>('/weclapp/tickets/duplicates/dismiss', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
  };
}
